import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom'; // Added useNavigate for type-based redirect
import { useSession } from '@supabase/auth-helpers-react'; // Import for session/user
import { useApi } from '../services/api'; // FIXED: Import useApi hook (adjust path if src/api.ts, not services)
import JobCreate from './JobCreate'; // Add this import for the JobCreate component
import { Card } from './ui/primitives';

interface Job {
  id: string;
  title: string;
  description: string;
  // Add other fields as needed from schema (e.g., status: JobStatus)
}

const Dashboard = () => {
  const session = useSession(); // Get user from Supabase session
  const navigate = useNavigate();
  const api = useApi(); // FIXED: Use the hook to get authenticated api instance
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!session) {
      navigate('/login'); // Redirect if no session
      return;
    }

    const userType = session.user?.app_metadata?.userType?.toUpperCase();
    console.log('User type from metadata:', userType); // Debug

    if (userType === 'EMPLOYER') {
      // Employer-specific logic if needed (e.g., fetch employer jobs)
    } else if (userType === 'CANDIDATE') {
      const fetchJobs = async () => {
        try {
          const { data } = await api.get('/public-jobs'); // FIXED: Use api from useApi
          setJobs(data);
        } catch (err: any) {
          console.error('Error fetching jobs:', err);
          setError('Failed to load jobs. Please refresh.');
        }
      };
      fetchJobs();
    } else {
      setError('Invalid user type. Please contact support.');
    }

    setLoading(false);
  }, [session, navigate, api]); // FIXED: Add api to deps (hook result is stable, but safe)

  if (loading) return <div className="min-h-screen bg-surface-page p-6" aria-live="polite">Loading...</div>;
  if (error) return <div className="min-h-screen bg-surface-page p-6 text-danger" aria-live="assertive">{error}</div>;
  if (!session?.user) return <div className="min-h-screen bg-surface-page p-6" aria-live="assertive">Error loading user data</div>;

  const userType = session.user.app_metadata?.userType?.toUpperCase();

  return (
    <div className="min-h-screen bg-surface-page p-4 md:p-6 space-y-4">
      <Card>
        <h1 className="text-[22px] md:text-page-title font-display font-bold text-txt-primary">
          Welcome, {session.user.email}
        </h1>
        <p className="text-body text-txt-secondary">User Type: {userType}</p>
        <p className="text-small text-txt-muted">Created: {session.user.created_at}</p>
      </Card>
      {/* Add more user info as needed */}
      <div className="mt-4">
        {userType === 'EMPLOYER' ? (
          <Card>
            <h2 className="text-section-title font-display font-bold text-txt-primary mb-3">Create Job</h2>
            <JobCreate />
          </Card>
        ) : (
          <Card>
            <h2 className="text-section-title font-display font-bold text-txt-primary mb-3">Available Jobs</h2>
            {jobs.length > 0 ? (
              <ul className="space-y-3">
                {jobs.map((job) => (
                  <li key={job.id} className="iv-card">
                    <h3 className="text-card-title font-semibold text-txt-primary">{job.title}</h3>
                    <p className="text-body text-txt-secondary mt-1">{job.description}</p>
                    <Link to={`/jobs/${job.id}/apply`} className="text-brand-600 font-medium mt-2 inline-block" aria-label={`Apply for ${job.title}`}>Apply</Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-body text-txt-secondary">No jobs available.</p>
            )}
          </Card>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
